// src/components/crm/support/TicketList.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, Filter, PlusCircle, ChevronRight, User, 
  Mail, Phone, Calendar, Tag, AlertCircle, 
  MessageSquare, Clock, MessageCircle, X
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { 
  collection, query, where, getDocs, limit
} from 'firebase/firestore';
import { db } from '../../../firebase/config';

const TicketList = () => {
  const { currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [error, setError] = useState(null);

  // Fetch tickets from Firestore for signed-in user only
  useEffect(() => {
    const fetchTickets = async () => {
      if (!currentUser) {
        setTickets([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      
      try {
        const ticketsQuery = query(
          collection(db, 'tickets'),
          where('createdBy', '==', currentUser.uid),
          limit(300)
        );
        const snapshot = await getDocs(ticketsQuery);

        const ticketData = snapshot.docs.map((ticketDoc) => {
          const data = ticketDoc.data();
          return {
            id: ticketDoc.id,
            subject: data.subject || 'Untitled Ticket',
            customerName: data.customerName || 'Unknown Customer',
            customerEmail: data.customerEmail || '',
            company: data.company || '',
            status: (data.status || 'new').toLowerCase(),
            priority: (data.priority || 'medium').toLowerCase(),
            category: data.category || 'General',
            assignedTo: data.assignedToName || data.assignedTo || 'Unassigned',
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt ? new Date(data.createdAt) : new Date()),
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : (data.updatedAt ? new Date(data.updatedAt) : new Date()),
            messages: data.conversation?.messages?.length || data.messages || 0
          };
        });

        ticketData.sort((a, b) => {
          const aTime = a.updatedAt instanceof Date ? a.updatedAt.getTime() : 0;
          const bTime = b.updatedAt instanceof Date ? b.updatedAt.getTime() : 0;
          return bTime - aTime;
        });

        setTickets(ticketData);
      } catch (err) {
        console.error("General error fetching tickets:", err);
        setError('Failed to load tickets.');
        setTickets([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchTickets();
  }, [currentUser]);

  // Get filtered tickets based on search term
  const getFilteredTickets = () => {
    let filtered = tickets;

    if (filterStatus !== 'all') {
      filtered = filtered.filter(ticket => ticket.status === filterStatus);
    }

    if (!searchTerm) return filtered;
    
    return filtered.filter(ticket => 
      ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (ticket.company && ticket.company.toLowerCase().includes(searchTerm.toLowerCase())) ||
      ticket.customerEmail.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Status and priority display info functions...
  const getStatusInfo = (status) => {
    switch (status) {
      case 'new':
        return { label: 'New', color: 'bg-blue-100 text-blue-800' };
      case 'in_progress':
        return { label: 'In Progress', color: 'bg-yellow-100 text-yellow-800' };
      case 'waiting':
        return { label: 'Waiting', color: 'bg-purple-100 text-purple-800' };
      case 'resolved':
        return { label: 'Resolved', color: 'bg-green-100 text-green-800' };
      case 'closed':
        return { label: 'Closed', color: 'bg-gray-100 text-gray-800' };
      default:
        return { label: status, color: 'bg-gray-100 text-gray-800' };
    }
  };

  const getPriorityInfo = (priority) => {
    switch (priority) {
      case 'high':
        return { label: 'High', color: 'text-red-600' };
      case 'medium':
        return { label: 'Medium', color: 'text-orange-600' };
      case 'low':
        return { label: 'Low', color: 'text-green-600' };
      default:
        return { label: priority, color: 'text-gray-600' };
    }
  };

  return (
    <div>
      {/* Search and Actions Bar */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            className="pl-10 pr-4 py-2.5 w-full rounded-lg text-sm border border-gray-200 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
            placeholder="Search tickets by subject, customer, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="flex gap-3">
          <select 
            className="px-4 py-2.5 rounded-lg border border-gray-200 text-gray-600 bg-white focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="in_progress">In Progress</option>
            <option value="waiting">Waiting</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
          
          <Link
            to="/crm/support/new"
            className="px-4 py-2.5 rounded-lg bg-[#190192] text-white flex items-center hover:bg-[#2C0DB5]"
          >
            <PlusCircle size={18} className="mr-2" />
            Create Ticket
          </Link>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 text-red-700">
          <p className="flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            {error}
          </p>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
        </div>
      ) : (
        <>
          {/* Empty State */}
          {tickets.length === 0 ? (
            <div className="bg-gray-50 rounded-lg py-10 px-6 text-center">
              <MessageCircle size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No support tickets found</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                You haven't created any support tickets yet. Click the 'Create Ticket' button to get started.
              </p>
              <Link
                to="/crm/support/new"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#190192] hover:bg-[#2C0DB5]"
              >
                <PlusCircle size={16} className="mr-2" />
                Create Your First Ticket
              </Link>
            </div>
          ) : getFilteredTickets().length === 0 ? (
            <div className="bg-gray-50 rounded-lg py-10 px-6 text-center">
              <MessageCircle size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No matching tickets</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                We couldn't find any tickets matching your search criteria. Try adjusting your search or filters.
              </p>
              <button
                onClick={() => { setSearchTerm(''); setFilterStatus('all'); }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            /* Tickets List/Table */
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ticket
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Assigned To
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Updated
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getFilteredTickets().map((ticket) => {
                      const statusInfo = getStatusInfo(ticket.status);
                      const priorityInfo = getPriorityInfo(ticket.priority);
                      
                      return (
                        <tr key={ticket.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <div className="flex items-start">
                                <div className={`flex-shrink-0 h-5 w-5 rounded-full ${
                                  ticket.priority === 'high' ? 'bg-red-500' :
                                  ticket.priority === 'medium' ? 'bg-orange-500' : 'bg-green-500'
                                } mr-2 mt-0.5`} />
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{ticket.subject}</div>
                                  <div className="text-xs text-gray-500 mt-1 flex items-center">
                                    <MessageSquare size={12} className="mr-1" /> {ticket.messages} messages
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col">
                              <div className="text-sm font-medium text-gray-900">{ticket.customerName}</div>
                              <div className="text-xs text-gray-500">{ticket.company}</div>
                              <div className="text-xs text-gray-500 flex items-center mt-1">
                                <Mail size={12} className="mr-1" /> {ticket.customerEmail}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusInfo.color}`}>
                              {statusInfo.label}
                            </span>
                            <div className="text-xs mt-1 font-medium flex items-center">
                              <AlertCircle size={12} className={`mr-1 ${priorityInfo.color}`} />
                              <span className={priorityInfo.color}>{priorityInfo.label} Priority</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                              {ticket.category}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {ticket.assignedTo}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 flex items-center">
                            <Clock size={14} className="mr-1 text-gray-400" /> {formatDate(ticket.updatedAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <Link
                              to={`/crm/support/${ticket.id}`}
                              className="text-[#190192] hover:text-[#2C0DB5] inline-flex items-center"
                            >
                              View <ChevronRight size={16} className="ml-1" />
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TicketList;
